import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./navBar.module.css";
import { useAuth } from "../../../context/AuthContext";
import sadMail from "../../../assets/images/question-mark.png";

const NavBar: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [scrolled, setScrolled] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      if (window.innerWidth >= 640) setMenuOpen(false);
    };
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) setScrolled(isScrolled);
    };
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  const handleNavigation = (route: string) => {
    navigate(route);
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const handleLogoutConfirm = async () => {
    await logout();
    setShowLogoutConfirm(false);
    navigate("/signin");
  };

  const navClass = `sticky top-0 z-50 flex items-center justify-between px-6 sm:px-8 md:px-16 py-4 rounded-b-[35px] transition-all duration-300 ${
    scrolled ? `${styles.scrolled} shadow-md` : "bg-transparent"
  }`;

  return (
    <>
      {/* 🔹 Logout Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="relative bg-white rounded-2xl shadow-xl p-8 w-[90%] sm:w-[450px] text-center animate-fadeIn border border-gray-100">
            <div className="flex justify-center mb-4">
              <img
                src={sadMail}
                alt="Logout Illustration"
                className="w-24 h-24 object-contain opacity-90"
              />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Are you sure you want to log out?
            </h3>
            <p className="text-gray-600 mb-6 text-sm sm:text-base">
              You’ll need to sign in again to rejoin the dance revolution 💃
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-2">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-5 py-3 rounded-xl text-gray-700 bg-gray-100 hover:bg-gray-200 transition text-sm font-medium shadow-sm"
              >
                I’d rather stay
              </button>
              <button
                onClick={handleLogoutConfirm}
                className="px-5 py-3 rounded-xl text-white text-sm font-medium shadow-md bg-gradient-to-r from-[#9F00FF] to-[#FB19E2] hover:opacity-90 transition"
              >
                Yes, log me out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔹 Navbar */}
      <nav className={navClass}>
        {/* Left: Logo */}
        <div className="flex items-center space-x-2">
          <img
            src="/vite.svg"
            alt="Beats Fussion Logo"
            className="w-8 h-8 sm:w-10 sm:h-10"
          />
          <Link
            to="/"
            onClick={() => handleNavigation("/")}
            className="text-lg sm:text-xl font-semibold text-black"
          >
            Beats Fussion
          </Link>
        </div>

        {/* Right: Navigation */}
        {!isMobile && (
          <div className="hidden sm:flex items-center space-x-5">
            {user ? (
              <>
                {/* 🟣 Role-based Nav */}

                {/* <div className="flex flex-col items-end text-sm mr-1">
                  <span className="font-semibold text-purple-700">
                    {user.name}
                  </span>
                  <span className="text-gray-600 capitalize">{user.role}</span>
                </div> */}

                {user.role === "viewer" && (
                  <button
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-white bg-opacity-80 text-purple-600 hover:bg-opacity-100 shadow-md"
                    onClick={() => handleNavigation("/voting")}
                  >
                    Voting
                  </button>
                )}
                <button
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-white bg-opacity-80 text-purple-600 hover:bg-opacity-100 shadow-md"
                  onClick={() => handleNavigation("/streaming")}
                >
                  Streaming
                </button>

                {/* Always visible Support */}
                <button
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-white bg-opacity-80 text-purple-600 hover:bg-opacity-100 shadow-md"
                  onClick={() => handleNavigation("/support")}
                >
                  Support
                </button>

                {/* User info */}

                {/* Logout */}
                <button
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-[#9F00FF] to-[#FB19E2] text-white shadow-md hover:opacity-90"
                  onClick={() => setShowLogoutConfirm(true)}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  className={`px-4 py-2 text-sm font-medium rounded-lg hover:bg-purple-700 shadow-md ${
                    scrolled
                      ? "bg-purple-600 text-white"
                      : "bg-white text-purple-600 hover:text-white"
                  }`}
                  onClick={() => handleNavigation("/waiting-list")}
                >
                  Join Waitlist
                </button>
                <button
                  className={`px-4 py-2 text-sm font-medium rounded-lg shadow-md ${
                    scrolled
                      ? "bg-purple-100 text-purple-600 hover:bg-purple-200"
                      : "bg-white bg-opacity-80 text-purple-600 hover:bg-opacity-100"
                  }`}
                  onClick={() => handleNavigation("/support")}
                >
                  Support
                </button>
                {/* <button
                  className={`px-4 py-2 text-sm font-medium rounded-lg shadow-md ${
                    scrolled
                      ? "bg-purple-100 text-purple-600 hover:bg-purple-200"
                      : "bg-white bg-opacity-80 text-purple-600 hover:bg-opacity-100"
                  }`}
                  onClick={() => handleNavigation("/signUp")}
                >
                  Get Started
                </button> */}
              </>
            )}
          </div>
        )}

        {/* Mobile Toggle */}
        {isMobile && (
          <div className={styles.navbarToggler}>
            <button
              className="text-purple-600 focus:outline-none"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16m-7 6h7"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Mobile Menu */}
        {menuOpen && isMobile && (
          <div
            className={`${styles.dropdownMenu} ${
              scrolled ? styles.scrolledDropdown : ""
            }`}
          >
            {user ? (
              <>
                {user.role === "viewer" && (
                  <button
                    className={styles.dropdownItem}
                    onClick={() => handleNavigation("/voting")}
                  >
                    Voting
                  </button>
                )}
                <button
                  className={styles.dropdownItem}
                  onClick={() => handleNavigation("/streaming")}
                >
                  Streaming
                </button>
                <button
                  className={styles.dropdownItem}
                  onClick={() => handleNavigation("/support")}
                >
                  Support
                </button>
                <button
                  className={`${styles.dropdownItem} text-red-500 font-semibold`}
                  onClick={() => setShowLogoutConfirm(true)}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  className={styles.dropdownItem}
                  onClick={() => handleNavigation("/waiting-list")}
                >
                  Join Waitlist
                </button>
                <button
                  className={styles.dropdownItem}
                  onClick={() => handleNavigation("/support")}
                >
                  Support
                </button>
                {/* <button
                  className={styles.dropdownItem}
                  onClick={() => handleNavigation("/signUp")}
                >
                  Get Started
                </button> */}
              </>
            )}
          </div>
        )}
      </nav>
    </>
  );
};

export default NavBar;
